import { useRef, useState } from "react";
import { Play, Pause, Download } from "lucide-react";

const fmt = (s) => {
  if (!isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

export const AudioPlayer = ({ src, title, category, testid }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
    } else {
      document.querySelectorAll("audio").forEach((el) => {
        if (el !== a) el.pause();
      });
      a.play().catch(() => {});
    }
  };

  return (
    <div
      data-testid={testid}
      className="flex items-center gap-4 border border-white/10 bg-black/40 px-4 py-3.5 transition-colors duration-300 hover:border-white/25"
    >
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onTimeUpdate={(e) => {
          setCur(e.target.currentTime);
          setProgress(e.target.duration ? (e.target.currentTime / e.target.duration) * 100 : 0);
        }}
        onLoadedMetadata={(e) => setDur(e.target.duration)}
      />
      <button
        data-testid={`${testid}-play-button`}
        onClick={toggle}
        aria-label={playing ? `Pause ${title}` : `Play ${title}`}
        className={`flex h-11 w-11 shrink-0 items-center justify-center border transition-colors duration-300 ${
          playing ? "border-gold bg-gold text-black" : "border-white/25 text-white hover:border-gold hover:text-gold"
        }`}
      >
        {playing ? <Pause className="h-4 w-4" /> : <Play className="ml-0.5 h-4 w-4" />}
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <span className="truncate font-syne text-sm font-semibold uppercase tracking-wide text-white">
            {title}
          </span>
          <span className="shrink-0 font-mono text-[10px] text-white/45">
            {fmt(cur)} / {fmt(dur)}
          </span>
        </div>
        <div className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.25em] text-gold">{category}</div>
        <div className="mt-2 flex items-center gap-2">
          <div className="flex h-4 items-end gap-[3px]" aria-hidden="true">
            {[0, 1, 2, 3, 4, 5, 6].map((i) => (
              <span
                key={i}
                className={`w-[2px] bg-gold/70 ${playing ? "eq-bar" : ""}`}
                style={{ height: "100%", transform: playing ? undefined : "scaleY(0.25)", animationDelay: `${i * 0.12}s` }}
              />
            ))}
          </div>
          <div className="h-px flex-1 bg-white/15">
            <div className="h-px bg-gold transition-[width] duration-200" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>
      <a
        href={src}
        download
        data-testid={`${testid}-download`}
        aria-label={`Download ${title}`}
        className="flex h-9 w-9 shrink-0 items-center justify-center border border-white/15 text-white/50 transition-colors hover:border-gold hover:text-gold"
      >
        <Download className="h-3.5 w-3.5" />
      </a>
    </div>
  );
};
